import React, { useCallback, useEffect, useRef, useState, type FC } from "react";
import clsx from "clsx";
import styles from "./InjectedIframe.module.scss";
import Icon from "../../../../../../shared-components/css-modules/toyota/atoms/Icon";
import Text from "../../../../../../shared-components/css-modules/toyota/atoms/Text";
import { CarConfigLabel } from "../../../../../utils/labelUtils";
import SrOnly from "../../../../../../../../common-deprecated/styles/v2/toyota/utilities/ScreenreaderOnly";

export type InjectedIframeProps = {
    url: string | null;
    timeout?: number; // Optional timeout in milliseconds, default to 10 seconds
};

export const DEFAULT_TIMEOUT = 10000; // 10 seconds

const InjectedIframe: FC<InjectedIframeProps> = ({ url, timeout = DEFAULT_TIMEOUT }) => {
    const [loaded, setLoaded] = useState<boolean>(false);
    const [error, setError] = useState<boolean>(false);
    const timer = useRef<NodeJS.Timeout | null>(null); // we can save timer in useRef and pass it to child

    const onLoad = useCallback(() => {
        setLoaded(true);
        if (timer.current) {
            clearTimeout(timer.current);
        }
    }, []);

    useEffect(() => {
        setLoaded(false);
        setError(false);

        timer.current = setTimeout(() => {
            if (!loaded) {
                setError(true);
            }
        }, timeout);

        return () => {
            if (timer.current) {
                clearTimeout(timer.current);
            }
        }; // Cleanup the timer on unmount or URL change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [url]);

    if (!url) {
        return null;
    }

    return (
        <div className={styles["embed-container"]}>
            {!loaded && !error && (
                <div className={styles["embed-loading-container"]}>
                    <Icon variant="trade" className={styles["embed-loading"]} />
                    <SrOnly>
                        <CarConfigLabel label="carConfigA11yLoading" />
                    </SrOnly>
                </div>
            )}
            {error && (
                <div className={styles["embed-error"]}>
                    <Text variant="apheleia-h3">
                        <strong>
                            <CarConfigLabel label="carConfigCalculatorErrorTitle" />
                        </strong>
                    </Text>
                    <Text variant="body-book" color="grey-7">
                        <CarConfigLabel label="carConfigCalculatorErrorDescription" />
                    </Text>
                </div>
            )}
            <iframe
                title="injected-content"
                src={url}
                className={clsx(styles["embed-iframe"], loaded && styles.loaded)}
                onLoad={onLoad}
            />
        </div>
    );
};

export default InjectedIframe;
